// Night Studio hero: a field of Persian letterforms drifting toward the camera, one instanced draw call.
// Browser only: HeroA calls this from an effect, and three.js is imported here dynamically so it is its own chunk.
import hasWebGL from "../shared/hasWebGL";

const GLYPHS: [string, number, string][] = [
  ["ف", 900, "Peyda"], ["و", 800, "Peyda"], ["ن", 900, "Peyda"], ["ت", 800, "Peyda"],
  ["ا", 900, "Peyda"], ["ک", 800, "Peyda"], ["گ", 900, "Peyda"], ["ی", 800, "Peyda"],
  ["ب", 900, "Peyda"], ["ح", 300, "ModamVF"], ["ع", 300, "ModamVF"], ["ش", 500, "ModamVF"],
  ["ژ", 300, "ModamVF"], ["پ", 900, "Peyda"], ["چ", 400, "ModamVF"], ["م", 800, "Peyda"],
];
const CELL = 256, GRID = 4, Z_MIN = -42, Z_RANGE = 50;

const VERT = /* glsl */ `
attribute vec3 aOffset; attribute float aScale; attribute float aCell; attribute float aSeed; attribute float aTint;
uniform float uTime; uniform float uZMin; uniform float uZRange;
varying vec2 vUv; varying float vDepth; varying float vTint;
void main() {
  float c = mod(aCell, 4.0); float r = floor(aCell / 4.0);
  vUv = (uv + vec2(c, 3.0 - r)) * 0.25;
  float a = sin(uTime * 0.12 + aSeed * 6.2831) * 0.22;
  vec2 p = mat2(cos(a), sin(a), -sin(a), cos(a)) * (position.xy * aScale);
  vec3 o = aOffset;
  o.x += sin(uTime * 0.11 + aSeed * 12.0) * 0.4;
  o.y += cos(uTime * 0.09 + aSeed * 7.0) * 0.4;
  o.z = uZMin + mod(o.z - uZMin + uTime * 0.35, uZRange);
  vec4 mv = modelViewMatrix * vec4(o + vec3(p, 0.0), 1.0);
  vDepth = -mv.z; vTint = aTint;
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
uniform sampler2D uMap; uniform vec3 uLight; uniform vec3 uBlue; uniform vec3 uInk; uniform float uFar;
varying vec2 vUv; varying float vDepth; varying float vTint;
void main() {
  float a = texture2D(uMap, vUv).a;
  float far = smoothstep(uFar * 0.3, uFar, vDepth);
  float near = smoothstep(2.0, 6.0, vDepth); // zero where glyphs wrap around, so recycled ones never pop in
  vec3 col = mix(mix(uLight, uBlue, vTint), uInk, far);
  float alpha = a * near * (1.0 - far * 0.94) * 0.88;
  if (alpha < 0.004) discard;
  gl_FragColor = vec4(col, alpha);
}`;

type Options = { getProgress: () => number; onLive: () => void; onLost: () => void };

// Resolves to a dispose function (a no-op when WebGL is unavailable or the hero unmounted while loading).
export async function startLetterField(host: HTMLElement, canvas: HTMLCanvasElement, { getProgress, onLive, onLost }: Options, isCancelled: () => boolean) {
  if (!hasWebGL()) return () => {};
  await Promise.all(GLYPHS.map(([ch, w, fam]) => document.fonts.load(`${w} 100px ${fam}`, ch))).catch(() => {});
  // @ts-ignore three ships without type declarations in this project
  const THREE = await import("three");
  if (isCancelled()) return () => {};

  // Draw the 16 glyphs once into a 4x4 atlas.
  const atlas = document.createElement("canvas");
  atlas.width = atlas.height = CELL * GRID;
  const ctx = atlas.getContext("2d")!;
  ctx.fillStyle = "#fff";
  ctx.textAlign = "center";
  GLYPHS.forEach(([ch, w, fam], i) => {
    ctx.font = `${w} ${CELL * 0.6}px ${fam}`;
    const m = ctx.measureText(ch);
    const x = (i % GRID) * CELL + CELL / 2 + (m.actualBoundingBoxLeft - m.actualBoundingBoxRight) / 2;
    const y = Math.floor(i / GRID) * CELL + CELL / 2 + (m.actualBoundingBoxAscent - m.actualBoundingBoxDescent) / 2;
    ctx.fillText(ch, x, y);
  });
  const tex = new THREE.CanvasTexture(atlas);
  tex.anisotropy = 4;

  const small = host.clientWidth < 768;
  const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: false, powerPreference: "low-power" });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, small ? 1.5 : 2));
  renderer.setClearColor(0x000000, 0);
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(55, 1, 0.1, 120);

  const N = small ? 90 : 170;
  const aspect = Math.max(0.45, Math.min(2.2, host.clientWidth / Math.max(1, host.clientHeight)));
  const W = 14.5 * aspect, H = 14;
  const off = new Float32Array(N * 3), scl = new Float32Array(N), cell = new Float32Array(N), seed = new Float32Array(N), tint = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    off[i * 3] = (Math.random() * 2 - 1) * W;
    off[i * 3 + 1] = (Math.random() * 2 - 1) * H;
    off[i * 3 + 2] = Z_MIN + Math.random() * Z_RANGE;
    scl[i] = Math.random() < 0.06 ? 3.6 + Math.random() * 1.6 : 0.8 + Math.random() * 1.8;
    cell[i] = Math.floor(Math.random() * GLYPHS.length);
    seed[i] = Math.random();
    tint[i] = Math.random() < 0.22 ? 1 : Math.random() * 0.2;
  }
  const base = new THREE.PlaneGeometry(1, 1);
  const geo = new THREE.InstancedBufferGeometry();
  geo.index = base.index;
  geo.setAttribute("position", base.attributes.position);
  geo.setAttribute("uv", base.attributes.uv);
  geo.setAttribute("aOffset", new THREE.InstancedBufferAttribute(off, 3));
  geo.setAttribute("aScale", new THREE.InstancedBufferAttribute(scl, 1));
  geo.setAttribute("aCell", new THREE.InstancedBufferAttribute(cell, 1));
  geo.setAttribute("aSeed", new THREE.InstancedBufferAttribute(seed, 1));
  geo.setAttribute("aTint", new THREE.InstancedBufferAttribute(tint, 1));
  geo.instanceCount = N;

  // Colors come from the design tokens; the shader writes sRGB directly, so undo three's linear conversion.
  const css = getComputedStyle(host);
  const token = (name: string, fallback: string) => {
    const c = new THREE.Color(css.getPropertyValue(name).trim() || fallback).convertLinearToSRGB();
    return new THREE.Vector3(c.r, c.g, c.b);
  };
  const uniforms = {
    uTime: { value: 0 },
    uMap: { value: tex },
    uZMin: { value: Z_MIN },
    uZRange: { value: Z_RANGE },
    uFar: { value: 46 },
    uLight: { value: token("--c-ink", "#eef1fa") },
    uBlue: { value: token("--c-accent", "#356bce") },
    uInk: { value: token("--c-bg", "#060a1c") },
  };
  const mat = new THREE.ShaderMaterial({ uniforms, vertexShader: VERT, fragmentShader: FRAG, transparent: true, depthWrite: false });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.frustumCulled = false;
  scene.add(mesh);

  const resize = () => {
    const w = host.clientWidth, h = host.clientHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / Math.max(1, h);
    camera.updateProjectionMatrix();
  };
  resize();
  const ro = new ResizeObserver(resize);
  ro.observe(host);

  // Pointer and device tilt shift the camera a little, so the field reads as depth.
  const target = { x: 0, y: 0 }, cur = { x: 0, y: 0 };
  const onPointer = (e: PointerEvent) => {
    const r = host.getBoundingClientRect();
    target.x = ((e.clientX - r.left) / r.width - 0.5) * 2;
    target.y = ((e.clientY - r.top) / r.height - 0.5) * 2;
  };
  const onTilt = (e: DeviceOrientationEvent) => {
    if (e.gamma == null || e.beta == null) return;
    target.x = Math.max(-1, Math.min(1, e.gamma / 30));
    target.y = Math.max(-1, Math.min(1, (e.beta - 45) / 30));
  };
  host.addEventListener("pointermove", onPointer, { passive: true });
  window.addEventListener("deviceorientation", onTilt, { passive: true });

  let raf = 0, last = 0, t = 0, prog = 0, live = false, visible = true, lost = false;
  const loop = (now: number) => {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    t += dt;
    cur.x += (target.x - cur.x) * 0.04;
    cur.y += (target.y - cur.y) * 0.04;
    prog += (getProgress() - prog) * 0.08; // the camera dollies in as the hero scrolls away
    uniforms.uTime.value = t;
    camera.position.set(cur.x * 1.4, -cur.y * 0.9, 10 - prog * 9);
    camera.lookAt(0, 0, camera.position.z - 50);
    renderer.render(scene, camera);
    if (!live) {
      live = true;
      onLive();
    }
    raf = requestAnimationFrame(loop);
  };
  const start = () => {
    if (raf || lost || !visible || document.hidden) return;
    last = performance.now();
    raf = requestAnimationFrame(loop);
    canvas.dataset.running = "1";
  };
  const stop = () => {
    cancelAnimationFrame(raf);
    raf = 0;
    canvas.dataset.running = "0";
  };
  const io = new IntersectionObserver(([e]) => {
    visible = e.isIntersecting;
    visible ? start() : stop();
  });
  io.observe(host);
  const onVisibility = () => (document.hidden ? stop() : start());
  document.addEventListener("visibilitychange", onVisibility);
  // context lost (backgrounded tab, GPU pressure): stop for good and let HeroA show the CSS letterforms again
  const onContextLost = () => {
    lost = true;
    stop();
    onLost();
  };
  canvas.addEventListener("webglcontextlost", onContextLost);
  start();

  return () => {
    stop();
    io.disconnect();
    ro.disconnect();
    host.removeEventListener("pointermove", onPointer);
    window.removeEventListener("deviceorientation", onTilt);
    document.removeEventListener("visibilitychange", onVisibility);
    canvas.removeEventListener("webglcontextlost", onContextLost);
    geo.dispose();
    base.dispose();
    mat.dispose();
    tex.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
  };
}
